import { Modal } from 'antd';
import { DvmExamOutput } from 'gql/types';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DvmExamForm } from '../DvmExamForm';

type Props = {
  donorId?: string;
  dvmExam?: DvmExamOutput;
  visible?: boolean;
  hide: () => void;
};

const DvmExamModal: FC<Props> = ({
  hide,
  dvmExam,
  visible,
  donorId,
  ...restProps
}) => {
  const { t } = useTranslation('donor.DvmExamForm');

  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const formRef = useRef<{ createHandler: () => void }>(null);
  const isEditMode = Boolean(dvmExam);

  useEffect(() => {
    if (!visible) {
      setIsFormValid(false);
      setIsFormDirty(false);
      setIsLoading(false);
    }
  }, [visible]);

  const closeImmediately = () => {
    hide();
  };
  const onCancelHandler = (e: any) => {
    if (isLoading) {
      return;
    }

    if (!isFormDirty) {
      closeImmediately();
      return;
    }

    e?.persist?.();

    Modal.confirm({
      title: t('unsavedChangesTitle'),
      content: t('unsavedChangesMessage'),
      okText: t('discardChanges'),
      cancelText: t('stayOnPage'),
      onOk: () => closeImmediately(),
    });
  };

  return (
    <Modal
      title={isEditMode ? t('editDvmExam') : t('newDvmExam')}
      visible={visible}
      onCancel={onCancelHandler}
      onOk={() => formRef.current?.createHandler()}
      okButtonProps={{
        disabled: !isFormValid || isLoading,
        loading: isLoading,
      }}
      cancelButtonProps={{
        disabled: isLoading,
      }}
      cancelText={t('close')}
      okText={isEditMode ? t('update') : t('create')}
      closable={!isLoading}
      keyboard={!isLoading}
      maskClosable={false}
      destroyOnClose
      {...restProps}
    >
      <DvmExamForm
        donorId={donorId}
        ref={formRef}
        hide={hide}
        dvmExam={dvmExam}
        setIsFormValid={setIsFormValid}
        setIsFormDirty={setIsFormDirty}
        setIsLoading={setIsLoading}
      />
    </Modal>
  );
};

export default DvmExamModal;
